import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Dialog from '@components/atoms/dialog/dialog';
import Icon from '@components/atoms/icon/icon';
import { MySizes as Sizes, MySizesStepType } from '@interfaces/models/mySizes';
import useWindowSize from '@hooks/use-window-size';
import {
  getSizesByCategory,
  filterMySizesForUniverse,
  createEmptyMySizes,
} from '@helpers/utils/catalog/my-sizes-helpers';
import EditingAppSheet from '@components/catalog/my-sizes/my-sizes/editing/editing-app-sheet/editing-app-sheet';
import EditingPopupMenu from '@components/catalog/my-sizes/my-sizes/editing/editing-popup-menu/editing-popup-menu';
import useIsWebview from '@hooks/use-is-webview';
import ConfirmDialog from '../confirm-dialog/confirm-dialog';
import FeedbackWrap from '../response-feedback/feedback-dialog';
import { RespStatusType } from '../response-feedback/types';
import MySizesFooter from '../footer/footer';
import SizeSection from '../size-section/size-section';
import UniverseTab from './universe-tab';
import { genId, sizeTypes, universesMap } from './sizesMap';
import styles from './editing.module.scss';

interface EditingProps {
  dialogOpen: boolean;
  setDialogOpen: (isOpen: boolean) => void;
  options: MySizesStepType[];
  onChange: (val: Sizes) => void;
  value: Sizes;
  dataFetching: boolean;
  respStatusType: RespStatusType;
  feedbackCallback: () => void;
}

const Editing: React.FC<EditingProps> = ({
  dialogOpen,
  setDialogOpen,
  options,
  onChange,
  feedbackCallback,
  value,
  dataFetching,
  respStatusType,
}) => {
  // 1, 2, 1-2 => index: 0, 1, 2 => woman, man, all
  const [universe, setUniverse] = useState(genId(value?.universe?.ids));
  const [womanSizeType, setWomanSizeType] = useState(sizeTypes[0][0]);
  const [manSizeType, setManSizeType] = useState(sizeTypes[1][0]);
  const [sizeTypeId, setSizeTypeId] = useState<string>(sizeTypes[0][0]);
  const [editValue, setEditValue] = useState(value);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDelConfirm, setOpenDelConfirm] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [respStatus, setRespStatus] = useState<RespStatusType>('success');
  const [isDelete, setIsDelete] = useState(false);
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { isWiderThanMd } = useWindowSize();
  const moreMenuRef = React.useRef<HTMLButtonElement>(null);
  const isWebview = useIsWebview();

  useEffect(() => {
    setDialogLoading(dataFetching);
    setRespStatus(respStatusType);
  }, [dataFetching, respStatusType]);

  const handleUniverseChange = (val: number) => {
    setUniverse(universesMap[val]);
  };

  useEffect(() => {
    if (universe === '2') {
      setSizeTypeId(manSizeType);
      return;
    }
    setSizeTypeId(womanSizeType);
  }, [universe, manSizeType, womanSizeType]);

  const handleWomanSizeTypeChange = (id: string) => {
    setSizeTypeId(id);
    setWomanSizeType(id);
  };
  const handleManSizeTypeChange = (id: string) => {
    setSizeTypeId(id);
    setManSizeType(id);
  };

  const womanSizes = useMemo(() => getSizesByCategory(options, 0, womanSizeType), [womanSizeType, options]);
  const manSizes = useMemo(() => getSizesByCategory(options, 1, manSizeType), [manSizeType, options]);

  // edit callback
  const saveCallback = () => {
    // filter the editvalue of specific universe
    const filteredValue = filterMySizesForUniverse(editValue, universe, sizeTypes, universesMap, options);
    onChange(filteredValue);
  };

  const openDeleteMysizesConfirmDialog = () => {
    setOpenDelConfirm(true);
  };

  // delete callback
  const handleDeleteMySizes = () => {
    setIsDelete(true);
    onChange(createEmptyMySizes());
    setOpenDelConfirm(false);
  };

  useEffect(() => {
    if (dialogOpen) {
      setEditValue(value);
      setUniverse(genId(value?.universe?.ids));
    }
  }, [dialogOpen, value]);

  return (
    <>
      <Dialog
        focusTrapActive={!isWebview}
        isOpen={dialogOpen}
        closeDialog={() => {
          feedbackCallback();
          setDialogOpen(false);
        }}
        styles={{
          content: {
            maxWidth: '654px',
            width: '100%',
            flexDirection: 'column',
            paddingBottom: '0.5rem',
            paddingTop: '1.7rem',
          },
        }}
        hasCloseIcon={!isWebview}
        hasCloseTxt
        customClass={styles.editingDialog}
      >
        {(modalDialogTitleId) => (
          <FeedbackWrap
            loading={dialogLoading}
            respStatus={respStatus}
            title={isDelete ? t('PLP.MYSIZES_DIALOG.DELETE_RESULT.TITLE') : undefined}
            feedbackCallback={() => {
              feedbackCallback();
              setDialogOpen(false);
            }}
            modalDialogTitleId={modalDialogTitleId}
          >
            <>
              <div className={styles.edit}>
                {isWiderThanMd && (
                  <button
                    className={clsx(styles.edit__deleteBtn, { [styles['edit__deleteBtn--show']]: openDelete })}
                    onMouseDown={openDeleteMysizesConfirmDialog}
                    type="button"
                  >
                    <Icon
                      name="trash-can"
                      className={styles.edit__deleteBtn__icon}
                      ariaHidden
                    />
                    {t('PLP.MYSIZES_DIALOG.DELETE_BUTTON')}
                  </button>
                )}
                <button
                  className={clsx(styles.edit__moreBtn, { [styles['edit__moreBtn--webview']]: isWebview })}
                  onClick={() => setOpenDelete(!openDelete)}
                  aria-label={tA11y('A11Y.ICON.MORE_MENU')}
                  ref={moreMenuRef}
                >
                  <Icon
                    name="more"
                    ariaHidden
                  />
                </button>
                {isWiderThanMd && openDelete && (
                  <EditingPopupMenu
                    onClick={openDeleteMysizesConfirmDialog}
                    onClose={() => {
                      moreMenuRef?.current?.focus();
                      setOpenDelete(false);
                    }}
                  />
                )}
                <h2 id={modalDialogTitleId}>{t('PLP.MYSIZES_DIALOG.EDIT.TITLE')}</h2>
              </div>

              <div className={styles.editStepsContainer}>
                <UniverseTab
                  value={universesMap.findIndex((e) => universe === e)}
                  onChange={handleUniverseChange}
                />

                {(universe === '1' || universe === '1-2') && (
                  <SizeSection
                    title={t('PLP.MYSIZES_DIALOG.WOMEN_SIZES')}
                    sizeType={womanSizeType}
                    sizes={womanSizes}
                    onSizeTypeChange={handleWomanSizeTypeChange}
                    optionsIndex={0}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    options={options}
                    universe={universe}
                  />
                )}

                {(universe === '2' || universe === '1-2') && (
                  <SizeSection
                    title={t('PLP.MYSIZES_DIALOG.MEN_SIZES')}
                    sizeType={manSizeType}
                    sizes={manSizes}
                    onSizeTypeChange={handleManSizeTypeChange}
                    optionsIndex={1}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    options={options}
                    universe={universe}
                  />
                )}
              </div>

              <MySizesFooter
                className={styles.editFooterStyleOverwrite}
                lastStep={true}
                lastStepCallback={saveCallback}
                hasChecked={true}
                handleNext={() => {}}
                allAvailableSteps={options}
                sizeId={sizeTypeId}
              />
            </>
          </FeedbackWrap>
        )}
      </Dialog>

      {!isWiderThanMd && (
        <EditingAppSheet
          open={openDelete}
          onDelete={() => {
            setOpenDelete(false);
            openDeleteMysizesConfirmDialog();
          }}
          onClose={() => setOpenDelete(false)}
        />
      )}

      <ConfirmDialog
        open={openDelConfirm}
        setOpen={setOpenDelConfirm}
        title={t('PLP.MYSIZES_DIALOG.DELETE_BUTTON')}
        content={t('PLP.MYSIZES_DIALOG.DELETE_CONFIRM')}
        confirmBtnTxt={t('PLP.MYSIZES_DIALOG.CONFIRM_DELETE')}
        onCancel={() => {
          setOpenDelConfirm(false);
        }}
        onConfirm={handleDeleteMySizes}
        focusTrapActive={!isWebview}
      />
    </>
  );
};

export default Editing;
